import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { WebSocketContext } from '../../websocket-context/websocket-provider';
import { GetVolContext } from '../../get-vol-provider';
import { CurrencyTypeEnum } from '../../../model/currency_type.enum';
import { SnapshotTypeEnum } from '../../../model/snapshot_type';
import { SnapshotRequest } from '../../model/snapshotData';
import usePersistentState from '../../use-persistent-state';

interface FootprintVolatilityCurvesProviderProps {
  children: ReactNode;
  providerKey?: string;
  currency?: CurrencyTypeEnum;
}

export interface IVolData {
  date: string;
  data: number[];
  hoursPast: number;
}

type DataType = {
  [SnapshotTypeEnum.CM]: any;
  [SnapshotTypeEnum.EE]: any;
};

type TimestampType = {
  [SnapshotTypeEnum.CM]: string;
  [SnapshotTypeEnum.EE]: string;
};

interface FootprintVolatilityCurvesContextType {
  data: DataType;
  timestamp: TimestampType;
  currency: CurrencyTypeEnum;
  setCurrency: React.Dispatch<React.SetStateAction<CurrencyTypeEnum>>;
  handleResetSubscription: (snapshotType: SnapshotTypeEnum, token: CurrencyTypeEnum) => void;

  expiry: string;
  setExpiry: (expiry: string) => void;
  volatilityData: IVolData[];
  expiryList: string[];
  liveDataTimestamp: string;
  historicalDataTimestamp: string;
  isLoading: boolean;
  hasDefaultToken: boolean;
}

export const VOL_KEYS = [
  '5P',
  '10P',
  '15P',
  '20P',
  '25P',
  '30P',
  '35P',
  '40P',
  'DN',
  '40C',
  '35C',
  '30C',
  '25C',
  '20C',
  '15C',
  '10C',
  '5C',
];

const HOURS_BEFORE = 24;

export const FootprintVolatilityCurvesContext = createContext(
  {} as FootprintVolatilityCurvesContextType,
);

export const FootprintVolatilityCurvesProvider = ({
  children,
  providerKey,
  currency: currencyProp,
}: FootprintVolatilityCurvesProviderProps) => {
  const { subscribeToChannel, sendMessage, isConnected } = useContext(WebSocketContext);

  const {
    data: volData,
    currency,
    setCurrency,
    hasDefaultToken,
    handleResetSubscription: resetSubscription,
  } = useContext(GetVolContext);

  const STATE_PATH = ['FOOTPRINT_VOLATILITY_CURVES_PROVIDER', `${providerKey || 'DEFAULT'}`];

  const [expiry, setExpiry] = usePersistentState(STATE_PATH, 'expiry', '');
  const [volatilityData, setVolatilityData] = useState<IVolData[]>([]);
  const [expiryList, setExpiryList] = useState<string[]>([]);
  const [liveDataTimestamp, setLiveDataTimestamp] = useState<string>('');
  const [historicalDataTimestamp, setHistoricalDataTimestamp] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const data = volData;
  const timestamp = {
    [SnapshotTypeEnum.CM]:
      volData?.[SnapshotTypeEnum.CM]?.params?.data?.timestamp ||
      volData?.[SnapshotTypeEnum.CM]?.result?.timestamp ||
      new Date().toISOString(),
    [SnapshotTypeEnum.EE]:
      volData?.[SnapshotTypeEnum.EE]?.params?.data?.timestamp ||
      volData?.[SnapshotTypeEnum.EE]?.result?.timestamp ||
      new Date().toISOString(),
  };

  const handleVolatilityData = (tenors: any, date: string, hoursPast: number, expiry: string) => {
    if (tenors && tenors[expiry]) {
      const tenorData = tenors[expiry];
      const mappedData = VOL_KEYS.map((key) => tenorData[key]);

      setVolatilityData((prevData) => {
        const existingIndex = prevData.findIndex((data) => data.date === date);
        if (existingIndex !== -1) {
          const updatedData = [...prevData];
          updatedData[existingIndex].data = mappedData;
          return updatedData;
        } else {
          return [...prevData, { date, data: mappedData, hoursPast }];
        }
      });

      setIsLoading(false);

      if (hoursPast === 0) {
        setLiveDataTimestamp(date);
      }
    }
  };

  const handleResponseHistoricalData = (
    response: any,
    pastDate: Date,
    hoursPast: number,
    expiry: string,
  ) => {
    const tenors = response?.result?.tenors;

    const date = pastDate.toISOString().replace('T', ' ').split('.')[0];
    handleVolatilityData(tenors, date, hoursPast, expiry);
    setHistoricalDataTimestamp(pastDate.toISOString().replace('T', ' ').split('.')[0]);
  };

  const getHistoricalData = (expiry: string) => {
    const now = new Date();
    for (let i = 0; i < HOURS_BEFORE; i++) {
      const pastDate = new Date(now.getTime() - i * 60 * 60 * 1000);
      const timestampValue = pastDate.toISOString();
      const requestId = `historic_data_${currency}_${timestampValue}_${providerKey}_v2`;

      const request = new SnapshotRequest(requestId, currency, SnapshotTypeEnum.EE, timestampValue);

      sendMessage(JSON.stringify(request));
      subscribeToChannel(requestId, (res: any) =>
        handleResponseHistoricalData(res, pastDate, i, expiry),
      );
    }
  };

  const makeGetData = (tenors: any, expiry: string) => {
    setIsLoading(true);
    handleVolatilityData(tenors, 'now', 0, expiry);
    getHistoricalData(expiry);
  };

  useEffect(() => {
    if (data && isConnected) {
      const tenors = data?.[SnapshotTypeEnum.EE]?.params?.data?.tenors;
      setVolatilityData([]);

      const expiryListData = Object.keys(tenors || {});

      setExpiryList(expiryListData);

      const currentExpiry = Array.isArray(expiry) ? expiry[0] : expiry;

      if (expiryListData.length > 0 && !expiryListData.includes(currentExpiry)) {
        setExpiry(expiryListData[0]);
        makeGetData(tenors, expiryListData[0]);
        return;
      }

      makeGetData(tenors, currentExpiry);
    }
  }, [data, expiry, isConnected]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (data && isConnected) {
        const tenors = data?.[SnapshotTypeEnum.EE]?.params?.data?.tenors;
        handleVolatilityData(tenors, 'now', 0, expiry);
      }
    }, 60000); // 60 seconds

    return () => clearInterval(intervalId);
  }, [data, expiry, isConnected]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (expiry) {
        getHistoricalData(expiry);
      }
    }, 3600000);

    return () => clearInterval(intervalId);
  }, [expiry]);

  useEffect(() => {
    if (currencyProp) {
      setCurrency(currencyProp);
    }
  }, [currencyProp, setCurrency]);

  return (
    <FootprintVolatilityCurvesContext.Provider
      value={{
        data,
        timestamp,
        currency,
        setCurrency,
        handleResetSubscription: resetSubscription,
        expiry,
        setExpiry,
        volatilityData,
        expiryList,
        liveDataTimestamp,
        historicalDataTimestamp,
        isLoading,
        hasDefaultToken,
      }}
    >
      {children}
    </FootprintVolatilityCurvesContext.Provider>
  );
};
